import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { rankingMovies, rankingTabs } from "@/mocks/movies";

const tabDesc: Record<string, string> = {
  heat: "按近期点开次数排序，反映当下的观看热度。",
  score: "按综合评分排序，代表更稳定的口碑表现。",
  fresh: "按上线时间先后排列，方便发现较新的条目。",
};

export default function RankingSection() {
  const [tab, setTab] = useState("heat");

  const list = useMemo(() => {
    const items = [...rankingMovies];
    if (tab === "score") return items.sort((a, b) => b.score - a.score);
    if (tab === "fresh") return items.reverse();
    return items;
  }, [tab]);

  return (
    <section id="ranking" className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-content mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <SectionHeading
              eyebrow="排行榜"
              title="不同维度下的高分条目"
              desc="同一批作品在不同排序下会呈现不同的参考价值。切换下方标签，可以分别按热度、口碑与上线时间查看。"
            />
          </Reveal>

          <Reveal delay={100}>
            <div className="inline-flex p-1 rounded-full bg-background-50 border border-background-200">
              {rankingTabs.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTab(item.id)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    tab === item.id
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                  aria-pressed={tab === item.id}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <p className="mt-4 text-sm text-foreground-600">{tabDesc[tab]}</p>

        <div className="mt-6 rounded-lg bg-background-50 border border-background-200 overflow-hidden">
          {list.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 md:gap-5 p-4 ${
                index !== 0 ? "border-t border-background-200" : ""
              }`}
            >
              <span
                className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-md font-heading font-bold text-sm ${
                  index === 0
                    ? "bg-primary-500 text-background-50"
                    : index === 1
                    ? "bg-accent-500 text-primary-950"
                    : "bg-background-200 text-foreground-700"
                }`}
              >
                {index + 1}
              </span>

              <div className="w-12 h-16 md:w-14 md:h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                <img src={item.poster} alt={`${item.title} 封面`} className="w-full h-full object-cover object-top" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px]">
                    {item.genre}
                  </span>
                </div>
                <p className="mt-1 text-xs text-foreground-500">{item.heat}</p>
                <p className="mt-1 text-sm text-foreground-600 hidden sm:block">{item.note}</p>
              </div>

              <div className="shrink-0 text-right">
                <p className="font-heading text-lg font-bold text-primary-600">{item.score.toFixed(1)}</p>
                <p className="text-[11px] text-foreground-400">综合评分</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}